// The shell every "add one of these" form sits in.
//
// ADDING USED TO EXPAND THE SECTION. Users and Locations pushed a grey panel into the middle
// of the tab, shoving the list down the page; Lines opened a modal; Devices left its picker
// open permanently. Three behaviours for one intention, and the inline ones are the worst of
// the three: the form appears where the eye is not, the list it belongs to jumps, and on a
// long tab the Save button can land below the fold. Kevin, 2026-08-13: "add xxxx should pop
// open a modal form and not expand the section."
//
// So: one shell, one place to fix the next thing wrong with it. The five modals that predate
// this each roll their own markup — worth folding in when one of them is next touched, not
// worth a sweep today.
//
// ESC AND THE BACKDROP BOTH CLOSE, because a modal that can only be dismissed by finding the
// Cancel button is a modal people learn to dread. Nothing here saves on dismiss: closing is
// always the safe direction.

import { useEffect } from 'react';

export default function FormModal({
  title,
  subtitle,
  onClose,
  children,
  // The action row. Given rather than composed so every one of these has its primary action
  // in the same corner.
  footer,
  size = 'md',
}) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const width = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' }[size] || 'max-w-lg';

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8"
      // The backdrop only — a click that started inside the panel must not close it, which is
      // what happens when you drag-select text in a field and release outside.
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`w-full ${width} rounded-lg bg-white shadow-xl`}
      >
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && (
          <div className="flex items-center gap-2 border-t border-gray-200 px-5 py-3">{footer}</div>
        )}
      </div>
    </div>
  );
}
